import {z} from 'zod';

import {isNilOrEmpty} from 'ramda-adjunct';

import {PhoneNumberSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {BATTERY_CHARGE_MAX, BATTERY_CHARGE_MIN} from '@dms/shared';

const commonAddressSchema = z.object({
  street: z.string(),
  descriptiveNumber: z.string().nullable(),
  orientationNumber: z.string().nullable(),
  city: z.string(),
  zip: z.string(),
  addressComplement: z.string().nullable(),
  country: z.string(),
  prefix: z.string().nullable(),
  district: z.string().nullable(),
  state: z.string().nullable(),
  postal: z.string().nullable(),
  coordinates: z
    .object({
      latitude: z.string(),
      longitude: z.string(),
    })
    .nullable(),
  externalAddressId: z.string().nullable(),
});

const priceSchema = z.object({
  withoutVat: z.number(),
  vat: z.number(),
  withVat: z.number(),
  vatType: z.string(),
});

const chargedPriceSchema = z.object({
  amount: z.number(),
  price: priceSchema.partial(),
});

const discountSchema = z.object({
  discount: priceSchema.partial(),
  percent: z.number(),
});

const rentPriceSchema = z.object({
  dailyRate: z.number(),
  paidDays: z.number(),
  freeDailyKilometers: z.number(),
  totalFreeKilometers: z.number(),
  rentalPrice: priceSchema,
  pricePerKilometer: priceSchema,
  deliveryPrice: priceSchema.partial(),
  otherPrice: priceSchema.partial(),
  discount: discountSchema.partial(),
  coPayment: priceSchema.partial(),
  contractPenalty: priceSchema.partial(),
  chargedKilometer: chargedPriceSchema.partial(),
  chargedFuel: chargedPriceSchema.partial(),
});

const requiredFields = ['withoutVat', 'withVat', 'vatType'] as const;

export const returnVehicleFormSchema = z
  .object({
    authorizedPersons: z.array(
      z.object({
        name: z.string(),
        phoneNumber: PhoneNumberSchema.optional().nullable(),
        idCardNumber: z.string().nullable(),
        drivingLicenceNumber: z.string().nullable(),
      })
    ),
    rentAddress: commonAddressSchema,
    returnAddress: commonAddressSchema.optional().nullable(),
    rentType: z.string(),
    fuelTank: z.number(),
    batteryCharge: z.number().int().min(BATTERY_CHARGE_MIN).max(BATTERY_CHARGE_MAX),
    actualMileage: z.number(),
    actualReturnDate: z.date(),
    equipment: z.array(z.string()),
    usedPriceGroup: z
      .object({
        rentalPriceGroupId: z.string(),
        rentalPriceGroupItemId: z.string(),
      })
      .partial(),
    conditionNote: z.string().optional(),
    rentPrice: rentPriceSchema,
    customPrice: priceSchema.partial().optional(),
    rentalOrderNote: z.string().optional(),
    isCustomPriceEnabled: z.boolean(),
    isAddressSame: z.boolean(),
    templateId: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isCustomPriceEnabled) {
      for (const field of requiredFields) {
        if (isNilOrEmpty(data.customPrice?.[field])) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
            path: [`customPrice.${field}`],
          });
        }
      }

      return;
    }

    if (data.rentPrice.pricePerKilometer) {
      for (const field of requiredFields) {
        if (isNilOrEmpty(data.rentPrice.pricePerKilometer[field])) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
            path: [`rentPrice.pricePerKilometer.${field}`],
          });
        }
      }
    }

    if (data.rentPrice.rentalPrice) {
      for (const field of requiredFields) {
        if (isNilOrEmpty(data.rentPrice.rentalPrice[field])) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('general.validations.fieldIsRequired'),
            path: [`rentPrice.rentalPrice.${field}`],
          });
        }
      }
    }
  });

export type ReturnVehicleFormValues = z.infer<typeof returnVehicleFormSchema>;
